import { FilterSelector } from "@/components/fields/forms/components/FilterSelector";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { TemplateField } from "@/integrations/supabase/templateEditor";
import { checkAffectedBranchingRules } from "@/utils/filterUtils";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { RuleSetsManager } from "./rules/RuleSetsManager";

interface FieldDisplayProps {
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  isFirst: boolean;
  isLast: boolean;
  templateId: string;
  availableFields: Array<{ fieldId: string; name: string }>;
  templateField?: TemplateField;
}

export const FieldDisplay = ({
  onMoveUp,
  onMoveDown,
  onRemove,
  isFirst,
  isLast,
  availableFields,
  templateField,
}: FieldDisplayProps) => {
  const [_affectedRuleSets, _setAffectedRuleSets] = useState<string[]>([]);

  const fieldDetails = templateField?.field;

  // Safely access the dataset ID, adding proper null checks
  const datasetId = fieldDetails?.primary_dataset_id;

  // fetch all the filters available for this field's dataset
  const { data: allFieldFilters } = useQuery({
    queryKey: ["primary-filters", datasetId],
    queryFn: async () => {
      if (!datasetId) return [];

      const { data, error } = await supabase
        .from("filters")
        .select(
          `*
          `,
        )
        .eq("dataset_id", datasetId)
        .order("name");

      if (error) throw error;
      return data;
    },
    enabled: !!datasetId,
  });

  const { data: customFilters, refetch: refetchCustomFilters } = useQuery({
    queryKey: ["template-field-filters", templateField?.id],
    queryFn: async () => {
      if (!templateField?.id) return [];

      const { data, error } = await supabase
        .from("template_field_filters")
        .select("filter_id, is_secondary")
        .eq("template_field_id", templateField.id);

      if (error) throw error;
      return data;
    },
    enabled: !!templateField?.id,
  });

  const handleFilterChange = async (filterId: string) => {
    if (!templateField?.id) return;

    try {
      if (filterId === "none") {
        const { error: deleteError } = await supabase
          .from("template_field_filters")
          .delete()
          .eq("template_field_id", templateField.id);

        if (deleteError) throw deleteError;
      } else {
        const { error } = await supabase.from("template_field_filters").upsert(
          {
            template_field_id: templateField.id,
            filter_id: filterId,
          },
          {
            onConflict: "template_field_id,is_secondary",
          },
        );

        if (error) throw error;
      }

      await refetchCustomFilters();
      toast.success("Filter updated successfully");
    } catch (error) {
      console.error("Error updating filter:", error);
      toast.error("Failed to update filter");
    }
  };

  const checkAffectedRules = async (filterId: string) => {
    if (!fieldDetails?.id || !templateField?.template_id) return [];

    return checkAffectedBranchingRules(fieldDetails.id, templateField.template_id, filterId);
  };

  const handleFilterChangeWithWarning = async (value: string, affected: string[] = []) => {
    if (affected.length > 0) {
      toast.warning(
        `Warning: This filter change affects branching logic in the following fields: ${affected.join(", ")}. 
        The affected branching rules may need to be updated.`,
      );
    }
    await handleFilterChange(value);
  };

  // Use field.id instead of field_id to match the actual property in templateField
  const _filteredAvailableFields = availableFields.filter((field) => field.fieldId !== templateField?.field_id);

  const currentPrimaryFilter = customFilters?.find((f) => !f.is_secondary)?.filter_id;

  // If we don't have field details, show a fallback UI
  if (!fieldDetails) {
    return (
      <div className="flex flex-col space-y-2 p-4 border rounded-lg bg-background">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h4 className="font-medium">Loading field details...</h4>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-2 p-4 border rounded-lg bg-background">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h4 className="font-medium">{fieldDetails.name}</h4>
          <p className="text-sm text-muted-foreground">Type: {fieldDetails.type}</p>
          {/* Add null check for dataset property */}
          {fieldDetails.dataset && <p className="text-sm text-muted-foreground">{fieldDetails.dataset.name}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="icon" onClick={onMoveUp} disabled={isFirst}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={onMoveDown} disabled={isLast}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <FilterSelector
          label="Primary Filter"
          value={currentPrimaryFilter || "none"}
          onChange={handleFilterChangeWithWarning}
          filters={allFieldFilters}
          readOnly={!allFieldFilters || allFieldFilters.length === 0}
          onFilterChange={checkAffectedRules}
        />
      </div>

      {templateField?.id && (
        <div className="mt-6">
          <RuleSetsManager templateFieldId={templateField.id} templateId={templateField.template_id} />
        </div>
      )}
    </div>
  );
};
